import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight, Users } from 'lucide-react';
import { dashboardService } from '@/app/services/dashboardService';
import { cn } from '@/lib/utils';

interface TeamPoolWidgetProps {
  className?: string;
}

/**
 * Shows the team-pool assignment breakdown for compliance tasks:
 * - My team pool: unclaimed tasks in the current user's teams (claimable now)
 * - Team pool (all): unclaimed tasks across all teams
 * - Claimed: team tasks already picked up by someone
 */
const TeamPoolWidget: React.FC<TeamPoolWidgetProps> = ({ className }) => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['compliance-dashboard'],
    queryFn: () => dashboardService.getComplianceDashboard(),
    refetchInterval: 60000,
  });

  const items = [
    {
      label: 'My Team Pool',
      value: data?.my_team_pool_count || 0,
      color: 'text-info',
      dotColor: 'bg-info',
      isUrgent: (data?.my_team_pool_count || 0) > 0,
      onClick: () => navigate('/tasks?team_pool=1'),
    },
    {
      label: 'Unclaimed (All Teams)',
      value: data?.team_pool_count || 0,
      color: 'text-warning',
      dotColor: 'bg-warning',
      onClick: () => navigate('/tasks?status=open'),
    },
    {
      label: 'Claimed',
      value: data?.team_assigned_count || 0,
      color: 'text-success',
      dotColor: 'bg-success',
      onClick: () => navigate('/tasks?status=open'),
    },
  ];

  if (isLoading) {
    return (
      <div className={cn('rounded-lg border bg-card', className)}>
        <div className="px-5 pt-4 pb-3">
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="grid grid-cols-3 gap-6 px-5 pb-4">
          {[1, 2, 3].map((i) => (
            <div key={i}>
              <Skeleton className="h-3 w-16 mb-2" />
              <Skeleton className="h-7 w-10" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border bg-card', className)}>
      <div className="px-5 pt-4 pb-3 flex items-center gap-2">
        <Users className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-medium text-muted-foreground">Team Task Pool</h3>
      </div>
      <div className="grid grid-cols-3 divide-x">
        {items.map((item) => (
          <button
            key={item.label}
            onClick={item.onClick}
            className="group px-5 pb-4 pt-1 text-left hover:bg-muted/30 transition-colors relative"
          >
            <div className="flex items-center gap-2 mb-0.5">
              <div
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  item.dotColor,
                  item.isUrgent && 'animate-pulse',
                )}
              />
              <span className="text-xs text-muted-foreground">{item.label}</span>
            </div>
            <p className={cn('text-2xl font-semibold tabular-nums', item.color)}>{item.value}</p>
            <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/0 group-hover:text-muted-foreground/50 transition-colors" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default TeamPoolWidget;
